import { useState, useRef, useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { usePermissionLayers, parseJsonOrThrow } from "./usePermissionLayers";

const API_BASE_URL = `${process.env.REACT_APP_BASE_URL}/api`;

/**
 * useSharedLevelConfig — the 3-tier permission editor bound to a SHARED
 * (named) admin level instead of a per-user personal one.
 *
 * Same layers + instant-save mutators as the Users tab (usePermissionLayers);
 * the only difference is the target: a fixed, already-existing level id, so
 * there is no lazy creation step.
 */
export const useSharedLevelConfig = (selectedCompany) => {
  const [selectedLevel, setSelectedLevel] = useState(null); // { id, name, ... }
  const [levelLoading, setLevelLoading] = useState(false);

  // guards against responses landing after the admin switched levels
  const activeLevelRef = useRef(null);

  const layers = usePermissionLayers({
    selectedCompany,
    resolveLevelId: useCallback(async () => {
      const id = activeLevelRef.current;
      if (!id) throw new Error("No admin level selected");
      return id;
    }, []),
    hasTarget: !!selectedLevel?.id,
  });
  const { ingestLevel, resetLayers, setError } = layers;

  // ── pick a level ──
  // Always refetch the level so the layers reflect the DB, not a possibly
  // stale row from the levels list (edits here don't refresh that list).
  const selectLevel = useCallback(async (level) => {
    if (!level?.id) return;
    activeLevelRef.current = level.id;
    setSelectedLevel(level);
    resetLayers();
    setLevelLoading(true);
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin-levels/${level.id}`, { method: "GET" });
      const data = await parseJsonOrThrow(res);
      if (activeLevelRef.current !== level.id) return; // admin moved on
      const fresh = data.adminLevel || level;
      setSelectedLevel(fresh);
      ingestLevel(fresh);
    } catch (err) {
      if (activeLevelRef.current === level.id) {
        // fall back to the (possibly stale) list row rather than a dead panel
        ingestLevel(level);
        setError(err.message || "Failed to load admin level");
      }
    } finally {
      if (activeLevelRef.current === level.id) setLevelLoading(false);
    }
  }, [ingestLevel, resetLayers, setError]);

  const clearSelectedLevel = useCallback(() => {
    activeLevelRef.current = null;
    setSelectedLevel(null);
    resetLayers();
  }, [resetLayers]);

  return {
    // selection
    selectedLevel, selectLevel, clearSelectedLevel, levelLoading,
    // permission layers (read)
    pageStates: layers.pageStates,
    features: layers.features,
    hiddenColumns: layers.hiddenColumns,
    // permission layers (write — instant save, optimistic)
    setPageState: layers.setPageState,
    toggleFeature: layers.toggleFeature,
    toggleColumn: layers.toggleColumn,
    // toasts
    error: layers.error, success: layers.success,
    setError, setSuccess: layers.setSuccess,
  };
};
